import Phaser from "phaser";
import { CAT_IDS, CATS } from "../data/cats";
import { DECORATIONS } from "../data/decorations";
import type { GameEventBus } from "../events";
import { getCropStage } from "../systems/CropSystem";
import type { CatId, CropPlotState, DecorationState, DecorationType } from "../types";
import type { GameStore } from "../state/GameStore";

const TILE_WIDTH = 176;
const TILE_HEIGHT = 88;
const ORIGIN_X = 744;
const ORIGIN_Y = 214;
const GRID_SIZE = 10;
const CAT_SCALE = 0.54;
const PLAYER_SCALE = 0.47;

type FarmBedVisual = {
  id: string;
  x: number;
  y: number;
  scale: number;
  cropScale: number;
  cropY: number;
  plotIds: string[];
};

const FARM_BEDS: FarmBedVisual[] = [
  { id: "farm-cabbage", x: 380, y: 485, scale: 0.76, cropScale: 0.44, cropY: -48, plotIds: ["plot-2"] },
  { id: "farm-carrot", x: 270, y: 615, scale: 0.7, cropScale: 0.43, cropY: -43, plotIds: ["plot-1"] },
  { id: "farm-seedlings", x: 550, y: 675, scale: 0.72, cropScale: 0.39, cropY: -45, plotIds: ["plot-5"] },
  { id: "farm-tomato", x: 785, y: 705, scale: 0.73, cropScale: 0.45, cropY: -47, plotIds: ["plot-6"] },
];

type CatPoint = { x: number; y: number };

const DECOR_TEXTURE: Record<DecorationType, string> = {
  fence: "env-fence",
  tree: "env-apple-tree",
  flowers: "env-flowers",
  catHouse: "env-cat-house",
};

const DECOR_SCALE: Record<DecorationType, number> = {
  fence: 0.5,
  tree: 0.72,
  flowers: 0.46,
  catHouse: 0.58,
};

// These seeded decorations belonged to the superseded free-form layout. Their
// state stays intact for save compatibility, while the approved 3x2 layout
// uses the intentional static fence boundary and edge flowers above.
const SUPERSEDED_STARTER_DECORATIONS = new Set(["decor-start-fence", "decor-start-flowers"]);

type PlacementMode = { type: DecorationType; existingId?: string } | null;

export class FarmSceneV2 extends Phaser.Scene {
  private store!: GameStore;
  private eventBus!: GameEventBus;
  private plotObjects = new Map<string, Phaser.GameObjects.Container>();
  private catSprites = new Map<CatId, Phaser.GameObjects.Sprite>();
  private catRoamTimers = new Map<CatId, Phaser.Time.TimerEvent>();
  private playerSprite?: Phaser.GameObjects.Sprite;
  private decorObjects = new Map<string, Phaser.GameObjects.Image>();
  private selectedPlotId: string | null = null;
  private selectedDecorationId: string | null = null;
  private placement: PlacementMode = null;
  private placementPreview?: Phaser.GameObjects.Image;

  constructor() {
    super("FarmScene");
  }

  create(): void {
    this.store = this.registry.get("store") as GameStore;
    this.eventBus = this.registry.get("events") as GameEventBus;

    this.add.image(743.5, 529, "farm-background").setDisplaySize(1487, 1058).setDepth(-10_000);
    this.createWorldObjects();
    this.createCatAnimations();
    this.createPlayerAnimations();
    this.renderPlots();
    this.renderDecorations();
    this.createPlayer();
    this.createCats();

    this.eventBus.on("plot:updated", ({ plotId }) => this.renderPlotById(plotId));
    this.eventBus.on("cat:fed", ({ catId }) => this.playCatReaction(catId as CatId, "feed"));
    this.eventBus.on("cat:petted", ({ catId }) => this.playCatReaction(catId as CatId, "pet"));
    this.eventBus.on("cat:owned", ({ catId }) => {
      this.createCats();
      this.playCatReaction(catId as CatId, "owned");
    });
    this.eventBus.on("player:created", () => this.createPlayer());
    this.eventBus.on("player:action", ({ action, targetX, targetY }) => this.playPlayerAction(action, targetX, targetY));
    this.eventBus.on("decoration:place-start", ({ type, existingId }) => {
      this.placement = { type: type as DecorationType, existingId };
      this.selectedDecorationId = null;
      this.createPlacementPreview(type as DecorationType);
    });
    this.eventBus.on("decoration:place-cancel", () => this.cancelPlacement());
    this.eventBus.on("decoration:updated", () => this.renderDecorations());
    this.store.subscribe(({ reason }) => {
      if (reason === "plant" || reason === "water" || reason === "harvest") this.renderPlots();
      if (reason.includes("decoration")) this.renderDecorations();
      if (reason === "create-player") this.createPlayer();
    });

    this.input.on("pointermove", (pointer: Phaser.Input.Pointer) => {
      if (!this.placement || !this.placementPreview) return;
      const cell = this.worldToCell(pointer.worldX, pointer.worldY);
      if (!cell) {
        this.placementPreview.setVisible(false);
        return;
      }
      const target = this.cellToWorld(cell.x, cell.y);
      const allowed = !this.isCellBlocked(cell.x, cell.y, this.placement.existingId);
      this.placementPreview
        .setVisible(true)
        .setPosition(target.x, target.y)
        .setTint(allowed ? 0xcfffb6 : 0xff8f9a)
        .setAlpha(0.7)
        .setDepth(target.y + 900);
    });

    this.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      if (this.placement) {
        const cell = this.worldToCell(pointer.worldX, pointer.worldY);
        if (cell) this.placeAtCell(cell.x, cell.y);
        return;
      }
      if (this.store.getSnapshot().player.created) this.movePlayerTo(pointer.worldX, pointer.worldY);
    });

    this.time.addEvent({ delay: 1_000, loop: true, callback: () => this.refreshGrowingPlots() });
    this.eventBus.emit("scene:ready", {});
  }

  private createWorldObjects(): void {
    const objects: Array<[string, number, number, number, number]> = [
      ["env-house", 865, 240, 1.62, 245],
      ["env-shop", 165, 285, 1.12, 300],
      ["env-pond", 145, 760, 1.03, 760],
      ["env-apple-tree", 1295, 205, 0.98, 205],
      ["env-cat-house", 1180, 420, 0.66, 455],
      ["env-birdhouse", 1240, 705, 0.53, 705],
      ["env-bridge", 370, 925, 0.8, 925],
      ["env-duck", 165, 820, 0.1, 910],
      ["env-watering-can", 480, 415, 0.34, 415],
      ["ui-food", 1040, 585, 0.44, 640],
      ["env-fence", 380, 315, 0.31, 315],
      ["env-fence", 490, 300, 0.31, 300],
      ["env-fence", 605, 315, 0.31, 315],
      ["env-fence", 1080, 355, 0.29, 355],
      ["env-fence", 1185, 350, 0.29, 350],
      ["env-fence", 1135, 850, 0.44, 850],
      ["env-fence", 1255, 885, 0.46, 885],
      ["env-fence", 1375, 920, 0.46, 920],
      ["env-flowers", 335, 350, 0.26, 350],
      ["env-flowers", 640, 350, 0.24, 350],
      ["env-flowers", 760, 380, 0.25, 380],
      ["env-flowers", 1040, 360, 0.25, 360],
      ["env-flowers", 1255, 520, 0.27, 520],
      ["env-flowers", 990, 560, 0.23, 560],
      ["env-flowers", 940, 780, 0.24, 780],
      ["env-flowers", 1080, 805, 0.27, 805],
      ["env-flowers", 310, 760, 0.26, 760],
      ["env-stepping-stones", 650, 370, 0.3, 385],
      ["env-stepping-stones", 780, 405, 0.3, 420],
      ["env-stepping-stones", 915, 445, 0.31, 455],
      ["env-stepping-stones", 995, 530, 0.31, 540],
      ["env-stepping-stones", 1010, 645, 0.31, 650],
      ["env-stepping-stones", 995, 755, 0.31, 760],
    ];

    for (const [texture, x, y, scale, depth] of objects) {
      this.add.image(x, y, texture).setScale(scale).setOrigin(0.5, 0.72).setDepth(depth);
    }
  }

  private renderPlots(): void {
    for (const object of this.plotObjects.values()) object.destroy(true);
    this.plotObjects.clear();
    const plotsById = new Map(this.store.getSnapshot().plots.map((plot) => [plot.id, plot]));
    for (const visual of FARM_BEDS) {
      const plots = visual.plotIds
        .map((plotId) => plotsById.get(plotId))
        .filter((plot): plot is CropPlotState => Boolean(plot));
      if (plots.length) this.renderFarmBed(visual, plots);
    }
  }

  private renderPlotById(_plotId: string): void {
    this.renderPlots();
  }

  private renderFarmBed(visual: FarmBedVisual, plots: CropPlotState[]): void {
    const container = this.add.container(visual.x, visual.y).setDepth(visual.y + 20);
    const bed = this.add.image(0, 0, "env-plot").setScale(visual.scale).setOrigin(0.5, 0.68);
    if (plots.some((plot) => this.selectedPlotId === plot.id)) bed.setTint(0xfff1b0);
    container.add(bed);

    const cropOffsets = plots.length > 1 ? [-38, 38] : [0];
    plots.forEach((plot, index) => {
      const stage = getCropStage(plot);
      if (plot.cropId && stage !== "empty") {
        const stageKey = stage === "dry" ? "seed" : stage;
        const clusterOffsets = stageKey === "seed" || stage === "sprout"
          ? [-52, -17, 18, 53]
          : plot.cropId === "cabbage"
            ? [-46, 0, 46]
            : plot.cropId === "carrot"
              ? [-34, 34]
              : [-30, 30];
        for (const clusterX of clusterOffsets) {
          const crop = this.add.image((cropOffsets[index] ?? 0) + clusterX, visual.cropY, `crop-${plot.cropId}-${stageKey}`)
            .setScale(visual.cropScale * (clusterOffsets.length > 2 ? 0.82 : 0.94))
            .setOrigin(0.5, 0.74);
          container.add(crop);
        }
      }
    });

    const hitWidth = Math.max(166, bed.displayWidth * 0.9);
    const hitHeight = Math.max(132, bed.displayHeight * 0.72);
    const hitSectionWidth = hitWidth / plots.length;

    plots.forEach((plot, index) => {
      const localX = -hitWidth / 2 + hitSectionWidth * (index + 0.5);
      const hitZone = this.add.zone(localX, 0, hitSectionWidth, hitHeight).setInteractive({ useHandCursor: true });
      hitZone.on("pointerdown", (_pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) => {
        if (this.placement) return;
        event.stopPropagation();
        this.selectedPlotId = plot.id;
        this.selectedDecorationId = null;
        this.eventBus.emit("plot:selected", { plotId: plot.id });
        this.renderPlots();
      });
      container.add(hitZone);
    });

    this.plotObjects.set(visual.id, container);
  }

  private refreshGrowingPlots(): void {
    const hasGrowingCrop = this.store.getSnapshot().plots.some((plot) => plot.cropId && plot.wateredAt);
    if (hasGrowingCrop) {
      this.renderPlots();
    }
  }

  private createCatAnimations(): void {
    for (const id of CAT_IDS) {
      const base = CATS[id].baseSprite;
      const definitions: Phaser.Types.Animations.Animation[] = [
        {
          key: `cat-${id}-idle`,
          frames: [{ key: `cat-${base}-idle` }, { key: `cat-${base}-sit` }],
          frameRate: 1.6,
          repeat: -1,
          yoyo: true,
        },
        {
          key: `cat-${id}-walk`,
          frames: [
            { key: `cat-${base}-walk-a` }, { key: `cat-${base}-idle` },
            { key: `cat-${base}-walk-b` }, { key: `cat-${base}-sit` },
          ],
          frameRate: 7,
          repeat: -1,
        },
        {
          key: `cat-${id}-trot`,
          frames: [{ key: `cat-${base}-walk-a` }, { key: `cat-${base}-walk-b` }],
          frameRate: 10,
          repeat: -1,
        },
        {
          key: `cat-${id}-eat`,
          frames: [{ key: `cat-${base}-sit` }, { key: `cat-${base}-eat` }, { key: `cat-${base}-eat` }, { key: `cat-${base}-sit` }],
          frameRate: 3.5,
          repeat: 1,
        },
        {
          key: `cat-${id}-pet`,
          frames: [{ key: `cat-${base}-sit` }, { key: `cat-${base}-pet` }, { key: `cat-${base}-happy` }, { key: `cat-${base}-pet` }],
          frameRate: 4.5,
          repeat: 1,
        },
        {
          key: `cat-${id}-happy`,
          frames: [{ key: `cat-${base}-pet` }, { key: `cat-${base}-happy` }, { key: `cat-${base}-sit` }],
          frameRate: 5,
          repeat: 1,
        },
        {
          key: `cat-${id}-sleep`,
          frames: [{ key: `cat-${base}-sleep-v2` }, { key: `cat-${base}-sleep-v2` }],
          frameRate: 1,
          repeat: -1,
        },
      ];
      for (const definition of definitions) {
        if (!this.anims.exists(definition.key!)) this.anims.create(definition);
      }
    }
  }

  private createPlayerAnimations(): void {
    const definitions: Phaser.Types.Animations.Animation[] = [
      { key: "player-idle", frames: [{ key: "player-idle-a" }, { key: "player-idle-b" }], frameRate: 2, repeat: -1, yoyo: true },
      { key: "player-walk", frames: [
        { key: "player-walk-a" }, { key: "player-walk-b" }, { key: "player-walk-c" }, { key: "player-walk-d" },
      ], frameRate: 8, repeat: -1 },
      { key: "player-plant", frames: [{ key: "player-idle-b" }, { key: "player-plant" }, { key: "player-plant" }, { key: "player-idle-a" }], frameRate: 5, repeat: 0 },
      { key: "player-water", frames: [{ key: "player-idle-b" }, { key: "player-water" }, { key: "player-water" }, { key: "player-idle-a" }], frameRate: 5, repeat: 0 },
      { key: "player-harvest", frames: [{ key: "player-idle-b" }, { key: "player-harvest" }, { key: "player-harvest" }, { key: "player-happy" }], frameRate: 5, repeat: 0 },
      { key: "player-feed", frames: [{ key: "player-idle-b" }, { key: "player-feed" }, { key: "player-feed" }, { key: "player-idle-a" }], frameRate: 5, repeat: 0 },
      { key: "player-pet", frames: [{ key: "player-idle-b" }, { key: "player-pet" }, { key: "player-pet" }, { key: "player-happy" }], frameRate: 5, repeat: 0 },
      { key: "player-happy", frames: [{ key: "player-idle-b" }, { key: "player-happy" }, { key: "player-happy" }, { key: "player-idle-a" }], frameRate: 5, repeat: 0 },
    ];
    for (const definition of definitions) {
      if (!this.anims.exists(definition.key!)) this.anims.create(definition);
    }
  }

  private createPlayer(): void {
    this.playerSprite?.destroy();
    this.playerSprite = undefined;
    const player = this.store.getSnapshot().player;
    if (!player.created) return;
    const point = this.cellToWorld(player.cellX, player.cellY);
    const bodyScale = player.bodyType === "soft" ? 0.97 : 1.03;
    const outfitTint = player.outfitColor === "peach" ? 0xffe1d6 : 0xffffff;
    const sprite = this.add.sprite(point.x, point.y, "player-idle-a")
      .setScale(PLAYER_SCALE * bodyScale)
      .setOrigin(0.5, 0.88)
      .setDepth(point.y + 82)
      .setTint(outfitTint)
      .setFlipX(player.direction === "left");
    sprite.play("player-idle");
    this.playerSprite = sprite;
  }

  private createCats(): void {
    for (const timer of this.catRoamTimers.values()) timer.remove(false);
    this.catRoamTimers.clear();
    for (const sprite of this.catSprites.values()) sprite.destroy();
    this.catSprites.clear();
    const ownedCats = this.store.getSnapshot().cats.filter((cat) => cat.owned);
    for (const [index, cat] of ownedCats.entries()) {
      const definition = CATS[cat.id];
      const point = definition.home;
      const sprite = this.add.sprite(point.x, point.y, `cat-${definition.baseSprite}-idle`)
        .setScale(CAT_SCALE)
        .setOrigin(0.5, 0.86)
        .setDepth(point.y + 70)
        .setTint(definition.tint);
      sprite.setInteractive({ useHandCursor: true });
      sprite.on("pointerdown", (_pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) => {
        if (this.placement) return;
        event.stopPropagation();
        this.eventBus.emit("cat:selected", { catId: cat.id });
      });
      sprite.on("pointerover", () => this.input.setDefaultCursor("pointer"));
      sprite.on("pointerout", () => this.input.setDefaultCursor("default"));
      this.catSprites.set(cat.id, sprite);
      sprite.play(`cat-${cat.id}-idle`);
      this.scheduleCatRoam(cat.id, 4_600 + index * 850);
    }
  }

  private scheduleCatRoam(catId: CatId, delay: number): void {
    this.catRoamTimers.get(catId)?.remove(false);
    const timer = this.time.delayedCall(delay, () => {
      const sprite = this.catSprites.get(catId);
      if (!sprite?.active) return;
      const target = this.pickCatPoint(catId);
      sprite.flipX = target.x < sprite.x;
      const distance = Phaser.Math.Distance.Between(sprite.x, sprite.y, target.x, target.y);
      const movingFast = distance > 105;
      sprite.play(`cat-${catId}-${movingFast ? "trot" : "walk"}`, true);
      const targetCell = this.worldToCell(target.x, target.y);
      if (targetCell) this.store.updateCatPosition(catId, targetCell.x, targetCell.y, movingFast ? "trot" : "walk");
      this.tweens.add({
        targets: sprite,
        x: target.x,
        y: target.y,
        duration: Phaser.Math.Between(movingFast ? 1_250 : 2_000, movingFast ? 2_050 : 3_000),
        ease: "Sine.easeInOut",
        onUpdate: () => sprite.setDepth(sprite.y + 70),
        onComplete: () => {
          sprite.play(`cat-${catId}-idle`, true);
          if (targetCell) this.store.updateCatPosition(catId, targetCell.x, targetCell.y, "idle");
          this.scheduleCatRoam(catId, 2_800 + Math.random() * 4_000);
        },
      });
    });
    this.catRoamTimers.set(catId, timer);
  }

  private playCatReaction(catId: CatId, reaction: "feed" | "pet" | "owned"): void {
    const sprite = this.catSprites.get(catId);
    if (!sprite) return;
    this.catRoamTimers.get(catId)?.remove(false);
    this.tweens.killTweensOf(sprite);
    const animation = reaction === "feed" ? "eat" : reaction === "pet" ? "pet" : "happy";
    sprite.play(`cat-${catId}-${animation}`, true);
    this.playPlayerAction(reaction === "owned" ? "happy" : reaction, sprite.x - 74, sprite.y + 10);
    if (reaction === "owned") {
      this.tweens.add({ targets: sprite, y: sprite.y - 18, duration: 230, yoyo: true, repeat: 1, ease: "Sine.easeOut" });
    }
    this.time.delayedCall(reaction === "feed" ? 2_000 : 1_650, () => {
      if (!sprite.active) return;
      sprite.play(`cat-${catId}-idle`, true);
      this.scheduleCatRoam(catId, 2_100);
    });
  }

  private movePlayerTo(worldX: number, worldY: number, onArrive?: () => void): void {
    const sprite = this.playerSprite;
    if (!sprite?.active) return;
    const targetX = Phaser.Math.Clamp(worldX, 330, 1_170);
    const targetY = Phaser.Math.Clamp(worldY, 330, 820);
    sprite.flipX = targetX < sprite.x;
    sprite.play("player-walk", true);
    const distance = Phaser.Math.Distance.Between(sprite.x, sprite.y, targetX, targetY);
    this.tweens.killTweensOf(sprite);
    this.tweens.add({
      targets: sprite,
      x: targetX,
      y: targetY,
      duration: Phaser.Math.Clamp(distance * 4.1, 420, 2_200),
      ease: "Sine.easeInOut",
      onUpdate: () => sprite.setDepth(sprite.y + 82),
      onComplete: () => {
        const cell = this.worldToCell(targetX, targetY);
        if (cell) this.store.updatePlayerPosition(cell.x, cell.y, sprite.flipX ? "left" : "right");
        sprite.play("player-idle", true);
        onArrive?.();
      },
    });
  }

  private playPlayerAction(action: "plant" | "water" | "harvest" | "feed" | "pet" | "happy", targetX?: number, targetY?: number): void {
    const sprite = this.playerSprite;
    if (!sprite?.active) return;
    let destinationX = targetX;
    let destinationY = targetY;
    if (destinationX === undefined || destinationY === undefined) {
      const bed = FARM_BEDS.find((visual) => this.selectedPlotId && visual.plotIds.includes(this.selectedPlotId));
      if (bed) {
        destinationX = bed.x + 92;
        destinationY = bed.y + 32;
      }
    }
    const play = () => {
      sprite.play(`player-${action}`, true);
      sprite.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => sprite.play("player-idle", true));
    };
    if (destinationX !== undefined && destinationY !== undefined) this.movePlayerTo(destinationX, destinationY, play);
    else play();
  }

  private renderDecorations(): void {
    for (const object of this.decorObjects.values()) object.destroy();
    this.decorObjects.clear();
    for (const decoration of this.store.getSnapshot().decorations) {
      if (SUPERSEDED_STARTER_DECORATIONS.has(decoration.id)) continue;
      this.renderDecoration(decoration);
    }
  }

  private renderDecoration(decoration: DecorationState): void {
    const point = this.cellToWorld(decoration.cellX, decoration.cellY);
    const image = this.add.image(point.x, point.y, DECOR_TEXTURE[decoration.type])
      .setScale(DECOR_SCALE[decoration.type])
      .setOrigin(0.5, 0.78)
      .setDepth(point.y + 35);
    if (this.selectedDecorationId === decoration.id) image.setTint(0xffefad);
    image.setInteractive(new Phaser.Geom.Rectangle(40, 60, 235, 300), Phaser.Geom.Rectangle.Contains);
    image.on("pointerdown", (_pointer: Phaser.Input.Pointer, _localX: number, _localY: number, event: Phaser.Types.Input.EventData) => {
      if (this.placement) return;
      event.stopPropagation();
      this.selectedDecorationId = decoration.id;
      this.selectedPlotId = null;
      this.eventBus.emit("decoration:selected", { decorationId: decoration.id });
      this.renderDecorations();
    });
    this.decorObjects.set(decoration.id, image);
  }

  private createPlacementPreview(type: DecorationType): void {
    this.placementPreview?.destroy();
    this.placementPreview = this.add.image(0, 0, DECOR_TEXTURE[type])
      .setScale(DECOR_SCALE[type])
      .setOrigin(0.5, 0.78)
      .setAlpha(0.7)
      .setVisible(false);
  }

  private cancelPlacement(): void {
    this.placement = null;
    this.placementPreview?.destroy();
    this.placementPreview = undefined;
  }

  private placeAtCell(cellX: number, cellY: number): void {
    if (!this.placement) return;
    const { type, existingId } = this.placement;
    if (this.isCellBlocked(cellX, cellY, existingId)) {
      this.eventBus.emit("toast", { message: "ช่องนี้มีสิ่งของอยู่แล้ว", tone: "warning" });
      return;
    }
    try {
      if (existingId) this.store.moveDecoration(existingId, cellX, cellY);
      else this.store.placeDecoration(type, cellX, cellY);
      this.cancelPlacement();
      this.renderDecorations();
      this.eventBus.emit("decoration:updated", {});
      this.eventBus.emit("decoration:place-cancel", {});
      this.eventBus.emit("toast", {
        message: existingId ? "ย้ายของตกแต่งแล้ว" : `วาง${DECORATIONS[type].name}แล้ว`,
        tone: "success",
      });
    } catch (error) {
      this.eventBus.emit("toast", {
        message: error instanceof Error ? error.message : "วางของตกแต่งไม่สำเร็จ",
        tone: "warning",
      });
    }
  }

  private isCellBlocked(cellX: number, cellY: number, ignoredDecorationId?: string): boolean {
    if (cellX < 0 || cellY < 0 || cellX >= GRID_SIZE || cellY >= GRID_SIZE) return true;
    const key = `${cellX},${cellY}`;
    const fixed = new Set([
      "0,7", "1,7", "0,8", "1,8", "8,0", "8,1", "9,0", "9,1", "9,2", "1,2", "1,3",
    ]);
    const state = this.store.getSnapshot();
    if (fixed.has(key)) return true;
    if (state.plots.some((plot) => plot.cellX === cellX && plot.cellY === cellY)) return true;
    return state.decorations.some((item) => item.id !== ignoredDecorationId && item.cellX === cellX && item.cellY === cellY);
  }

  private pickCatPoint(movingCatId: CatId): CatPoint {
    return Phaser.Utils.Array.GetRandom(CATS[movingCatId].roam) ?? CATS[movingCatId].home;
  }

  public getAnimationDebug(): {
    player: { key: string | null; isPlaying: boolean; x: number; y: number } | null;
    cats: Array<{ id: CatId; key: string | null; isPlaying: boolean; x: number; y: number }>;
  } {
    const player = this.playerSprite?.active
      ? {
          key: this.playerSprite.anims.currentAnim?.key ?? null,
          isPlaying: this.playerSprite.anims.isPlaying,
          x: Math.round(this.playerSprite.x),
          y: Math.round(this.playerSprite.y),
        }
      : null;
    const cats = [...this.catSprites.entries()].map(([id, sprite]) => ({
      id,
      key: sprite.anims.currentAnim?.key ?? null,
      isPlaying: sprite.anims.isPlaying,
      x: Math.round(sprite.x),
      y: Math.round(sprite.y),
    }));
    return { player, cats };
  }

  private cellToWorld(cellX: number, cellY: number): Phaser.Math.Vector2 {
    return new Phaser.Math.Vector2(
      ORIGIN_X + (cellX - cellY) * TILE_WIDTH / 2,
      ORIGIN_Y + (cellX + cellY) * TILE_HEIGHT / 2,
    );
  }

  private worldToCell(worldX: number, worldY: number): { x: number; y: number } | null {
    const localX = (worldX - ORIGIN_X) / (TILE_WIDTH / 2);
    const localY = (worldY - ORIGIN_Y) / (TILE_HEIGHT / 2);
    const x = Math.round((localY + localX) / 2);
    const y = Math.round((localY - localX) / 2);
    if (x < 0 || y < 0 || x >= GRID_SIZE || y >= GRID_SIZE) return null;
    return { x, y };
  }
}
